// ============================================================================
// Beacon — AI Deal Analyzer (Claude).
//
// Split for security: the deal-specific PROMPT is built here (pure, tested,
// runs in the browser where the engine lives); the Anthropic API KEY lives in
// the `analyze` Edge Function, which is a thin relay. The browser never sees
// the key, and the engine math never gets duplicated into Deno.
// ============================================================================

import type { Deal, Metrics } from "../types";
import { calc } from "../engine/engine";
import { money0, pct, x2 } from "./format";
import { isCloud, getSupabase } from "./supabase";
import { logUsage } from "./usage";

export const ANALYZER_SYSTEM =
  "You are a Section 8 / affordable-housing investment analyst reviewing a single Florida " +
  "property that has already been underwritten by a deterministic engine. You are given the " +
  "final numbers — do not recompute them. Write a concise, plain-English investment read for a " +
  "sponsor: lead with a verdict, then the 2–3 things that most drive it, the single biggest risk, " +
  "and one concrete next step. Be direct and specific to these numbers. This is decision-support, " +
  "not investment advice — never invent facts not present in the data.";

/** Pure: turn a deal + its computed metrics into the user prompt for Claude. */
export function buildAnalyzerPrompt(deal: Deal, m: Metrics): string {
  const g = m.gates;
  return [
    `PROPERTY: ${deal.name} — ${deal.addr || deal.market} (${deal.market}), stage ${deal.stage}.`,
    `Units: ${deal.u2c}×2BR @ ${money0(deal.u2r)}/mo, ${deal.u1c}×1BR @ ${money0(deal.u1r)}/mo. Built ${deal.yearBuilt}, roof age ${deal.roofAge}, flood zone ${deal.flood}.`,
    ``,
    `SCORE: ${m.score.gated ? "GATED — " : ""}${m.score.band}${m.score.gated ? "" : " (" + m.score.composite + "/100)"}.`,
    `Pillars — financial ${m.score.fin}, location ${m.score.loc}, compliance/execution ${m.score.ce}, deal quality ${m.score.dq}.`,
    ``,
    `RETURNS: cap-on-cost ${pct(m.capOnCost)}, DSCR ${x2(m.dscr)} (min ${x2(deal.dscrMin)}), debt yield ${pct(m.debtYield)}, cash-on-cash ${pct(m.coc)}, 5-yr IRR ${m.projIRR ? pct(m.projIRR) : "n/a"}, equity multiple ${x2(m.equityMult)}.`,
    `COST/VALUE: all-in ${money0(m.allIn)}, stabilized value ${money0(m.stabVal)} (${money0(m.stabVal - m.allIn)} vs cost), NOI ${money0(m.noi)}.`,
    `INSURANCE STRESS: DSCR falls to ${x2(m.dscrStress)} at the hard-market premium ${money0(deal.insStress)} (base ${money0(deal.insBase)}).`,
    `TAKEOUT: worst-case refinance gap ${money0(m.takeout[3].gap)} (negative = cash-in refinance needed).`,
    ``,
    `COMPLIANCE GATES:`,
    `A physical/NSPIRE: ${g.A.t} — ${g.A.txt}`,
    `B rent ceiling: ${g.B.t} — ${g.B.txt}`,
    `C program/PHA: ${g.C.t} — ${g.C.txt}`,
    `D legal/title: ${g.D.t} — ${g.D.txt}`,
    ``,
    `Write the read now. Under ~200 words. Do not restate every number — interpret them.`,
  ].join("\n");
}

export interface AnalysisResult {
  text: string;
  model?: string;
}

/**
 * Run the AI analysis. Cloud-only: the Anthropic key lives in the `analyze`
 * Edge Function. Throws if not in cloud mode or the function isn't deployed.
 */
export async function analyzeDeal(deal: Deal): Promise<AnalysisResult> {
  if (!isCloud()) throw new Error("AI analysis needs cloud mode (the Anthropic key lives in the Edge Function).");
  const sb = getSupabase()!;
  const prompt = buildAnalyzerPrompt(deal, calc(deal));
  const { data, error } = await sb.functions.invoke("analyze", {
    body: { system: ANALYZER_SYSTEM, user: prompt },
  });
  if (error) throw new Error(error.message || "analyze failed");
  void logUsage("AI (Claude)", "deal analysis");
  return data as AnalysisResult;
}
